import { api } from '../api/client'
import { WORTLAUT } from '../texte'
import { notiere } from './sendeprotokoll'

/**
 * Apple Music in our own page (#106) -- loading, signing in, playing.
 *
 * **Why this exists.** Apple's embedded player cannot keep a sign-in: Safari
 * needs the Storage Access API called *from inside the frame*, and that is
 * Apple's code, not ours. The only way a sign-in holds is MusicKit in **our**
 * page; then the account's token lives in our origin.
 *
 * **And it is the widest step outward this project takes.** Apple's frame was
 * shut in -- its own origin, a `sandbox` with reasoned tokens. A script in our
 * page is not: it sees our DOM, our storage, our cookies. Four things keep it
 * small, and each is a condition, not a taste:
 *
 * 1. **Nothing loads until someone taps an Apple Music card.** `prepare()` is
 *    called only from a tap -- the same build as `mp4box` on the first video.
 *    Who never taps sends nothing out.
 * 2. **No autoplay.** Playing is always the answer to a tap.
 * 3. **A way back.** `signOut()` exists and has a button on the card.
 * 4. **The developer token comes from the server**, fresh per call, and is
 *    valid for one hour, and carries an `origin`: copied elsewhere it is
 *    worthless. (1.71.1 tried without it as an experiment; it did not help,
 *    and 1.71.2 put it back -- see `MusicKitController::MIT_ORIGIN`.)
 *
 * **What it does not solve:** a whole track needs the listener's own active
 * Apple Music subscription.
 *
 * **1.71.0: every step reports on its own.** In 1.70.0 one `catch` took
 * `authorize()`, `setQueue()` and `play()` together and threw the reason
 * away. From the field, in the app on the home screen: Apple asked "allow
 * access?", the answer was yes -- and back in the app stood "did not work".
 * Which of the three failed was not to be read anywhere. Now each step writes
 * its name and its error into the trace under *Einstellungen › Diagnose*,
 * and so does every address our CSP blocks after the first tap.
 */

/**
 * **MusicKit is switched off, not removed** (1.73.0).
 *
 * On the device with 1.72.2 both ways worked: MusicKit played whole tracks
 * after its sign-in, and Apple's own player held a sign-in made inside it
 * once its frame got the page name. Apple's player shows the whole playlist,
 * MusicKit only our card -- and from use: "Anmelden in der Apple Karte reicht
 * und funktioniert auch". So the card shows the provider's player again.
 *
 * Kept, because it was asked for: "ich bin mir nicht sicher, ob man es nicht
 * doch braucht". With `false` nothing of this file runs (`Musikkarte` never
 * calls `prepare()`), the privacy page leaves the paragraph out, and the
 * server keeps `/api/music/token` and the CSP names. `true` brings the own
 * card with sign-in back -- then the privacy texts and `musik.mjs` need their
 * MusicKit parts again (both check this one constant).
 */
export const MUSICKIT_AKTIV = false

/** Apple's address, once here. It also stands in the CSP (`script-src`); `CspTest` reads it from this line. */
const SKRIPT = 'https://js-cdn.music.apple.com/musickit/v3/musickit.js'

/** What the app calls on MusicKit -- and nothing more. No package, no foreign type file. */
export interface MusicKitInstance {
  isAuthorized: boolean
  isPlaying?: boolean
  authorize: () => Promise<string>
  unauthorize: () => Promise<void>
  setQueue: (what: Record<string, string>) => Promise<unknown>
  play: () => Promise<void>
  pause: () => void | Promise<void>
  addEventListener?: (name: string, listener: () => void) => void
}

interface MusicKitGlobal {
  configure: (opt: { developerToken: string; app: { name: string; build: string } }) => Promise<MusicKitInstance>
  getInstance: () => MusicKitInstance | undefined
}

declare global {
  interface Window {
    MusicKit?: MusicKitGlobal
  }
}

/**
 * An error, written so that a person can read it in the trace.
 *
 * MusicKit rejects with its own objects (`MKError` with `errorCode`), not
 * always with an `Error`; `String(x)` would then say `[object Object]` --
 * exactly the kind of line that explains nothing.
 */
export function describeError(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`
  if (typeof error === 'object' && error !== null) {
    const e = error as Record<string, unknown>
    const parts = [e.name, e.errorCode, e.description, e.message].filter((p) => typeof p === 'string' && p !== '')
    if (parts.length > 0) return parts.join(': ')
    try {
      return JSON.stringify(error).slice(0, 200)
    } catch {
      // A circular object: then at least its kind, and no invented sentence.
      return Object.prototype.toString.call(error)
    }
  }
  return String(error)
}

/** One line in the trace, always with the same prefix so it is found. */
export function traceMusic(step: string, error?: unknown): void {
  notiere(error === undefined ? `MusicKit: ${step}` : `MusicKit: ${step}: ${describeError(error)}`)
}

let loading: Promise<MusicKitInstance> | null = null
let instance: MusicKitInstance | null = null
let cspWatched = false

/**
 * Every address our CSP blocks, into the trace -- from the first tap on.
 *
 * **Apple publishes no list** of the hosts MusicKit needs to play; looked for
 * and not found. Guessing a fourth name into `.htaccess` is the next round of
 * the same; a line "blocked: media-src https://…" names it. Installed only
 * after the first tap, like everything else here.
 */
function watchCsp(): void {
  if (cspWatched) return
  cspWatched = true
  document.addEventListener('securitypolicyviolation', (e) => {
    notiere(`CSP gesperrt: ${e.violatedDirective} ${e.blockedURI}`)
  })
}

/**
 * **Our page tells Apple its name -- from the first tap on, never before**
 * (#106, 1.72.1).
 *
 * `server/.htaccess` sends `Referrer-Policy: same-origin`: nothing goes out
 * with a request to a foreign server. For Apple's sign-in that is the one
 * thing that was missing. From Apple's forum, the same step and the same
 * words (`AUTHORIZATION_ERROR: Unauthorized` after a sign-in that went
 * through), and the fix there was the referrer policy alone: `no-referrer`
 * failed, `origin` worked. Everything else on our side had been ruled out,
 * and dropping `origin` from the token (1.71.1) could not help -- Apple
 * checks the page, and without a referrer it has none to check.
 *
 * **Built like the exception for the map tiles**: only the name of the page
 * (`strict-origin-when-cross-origin` sends the origin to a foreign server,
 * never the path, so never the address of a trip), and only where it is
 * needed. A `<meta name="referrer">` set here changes the policy of the
 * document from this moment on; until then the header holds. Who never taps
 * an Apple card sends nothing, as before.
 *
 * **Narrowed from the other side (1.72.2).** MusicKit's own requests
 * (sign-in window, playback) come at unknown times, so the document policy
 * cannot be limited to them. Our own foreign elements carry their own rule
 * instead: the GIF previews, Spotify's frame and the covers say
 * `referrerPolicy="no-referrer"`, the map tiles keep theirs
 * (`kacheloptionen()`). What carries the page name is Apple: its script after
 * the first tap, and its frame always (only the origin; see `Musikkarte`,
 * where the device showed the sign-in in the frame holds only with it).
 *
 * **Confirmed on the device** (1.72.1): with the page name the sign-in went
 * through, the trace said "angemeldet" and "spielt".
 */
function nameOurPage(): void {
  if (document.querySelector('meta[name="referrer"][data-musickit]') !== null) return
  const meta = document.createElement('meta')
  meta.name = 'referrer'
  meta.content = 'strict-origin-when-cross-origin'
  meta.setAttribute('data-musickit', '')
  document.head.appendChild(meta)
}

function loadScript(): Promise<void> {
  return new Promise((done, failed) => {
    if (window.MusicKit !== undefined) {
      done()
      return
    }
    const el = document.createElement('script')
    el.src = SKRIPT
    el.async = true
    el.addEventListener('load', () => done())
    el.addEventListener('error', () => failed(new Error(WORTLAUT.technik.musik.skriptFehlt)))
    document.head.appendChild(el)
  })
}

/**
 * Token, script, `configure()` -- everything that may wait.
 *
 * **Nothing here opens a window**, so it is free to `await`. What opens one
 * is `authorize()`, and that is called by the card as the first line of a
 * tap on an instance that is already here (`preparedInstance()`). In 1.70.0
 * two `await`s stood before it, while the comment above claimed the opposite.
 *
 * `configure()` also restores an earlier sign-in from our origin's storage;
 * `isAuthorized` is therefore true right after it for someone who signed in
 * yesterday.
 */
export function prepare(): Promise<MusicKitInstance> {
  if (loading !== null) return loading
  watchCsp()
  nameOurPage()
  traceMusic('wird geladen')

  loading = (async () => {
    const { token } = await api.get<{ token: string }>('/api/music/token')
    await loadScript()
    const mk = window.MusicKit
    if (mk === undefined) throw new Error(WORTLAUT.technik.musik.skriptFehlt)
    const ready = await mk.configure({
      developerToken: token,
      // The name stands in Apple's sign-in window -- a person reads it.
      app: { name: WORTLAUT.allgemein.name, build: '1' },
    })
    instance = ready
    ready.addEventListener?.('playbackStateDidChange', () => {
      setState({ ...state, playing: ready.isPlaying === true })
    })
    ready.addEventListener?.('authorizationStatusDidChange', () => {
      remember(ready.isAuthorized)
      setState({ ...state, signedIn: ready.isAuthorized })
    })
    traceMusic(`bereit, angemeldet: ${ready.isAuthorized ? 'ja' : 'nein'}`)
    setState({ ...state, signedIn: ready.isAuthorized })
    return ready
  })().catch((error: unknown) => {
    traceMusic('Laden fehlgeschlagen', error)
    // A failed attempt must not block the next one.
    loading = null
    throw error
  })

  return loading
}

/** The instance, if `prepare()` has finished -- without loading anything. */
export function preparedInstance(): MusicKitInstance | null {
  return instance
}

/**
 * What to play, per kind.
 *
 * **All four kinds, and the playlist is the normal case** ("es werden ja
 * nicht nur einzelne Titel sondern ganze Playlists bereitgestellt").
 * `track_ref` wins: `?i=` names one track in an album, and the post meant
 * that track, not the record.
 */
export function queueFor(kind: string, ref: string, trackRef: string): Record<string, string> | null {
  if (trackRef !== '') return { song: trackRef }
  if (kind === 'song') return { song: ref }
  if (kind === 'album') return { album: ref }
  if (kind === 'playlist') return { playlist: ref }
  // An artist has no queue -- there only the button to the outside leads on.
  return null
}

/*
 * **Whether this browser signed in before**, as a note in `localStorage`.
 *
 * Only a convenience: it decides whether a tap on a card goes straight to
 * MusicKit or first offers the choice. MusicKit keeps the real sign-in
 * itself; if the note is missing or storage throws (private window), the way
 * over the choice works just the same.
 */
const REMEMBER_KEY = 'tb-musickit-angemeldet'

export function rememberedSignIn(): boolean {
  try {
    return localStorage.getItem(REMEMBER_KEY) === '1'
  } catch {
    return false
  }
}

function remember(on: boolean): void {
  try {
    if (on) localStorage.setItem(REMEMBER_KEY, '1')
    else localStorage.removeItem(REMEMBER_KEY)
  } catch {
    // Private window: then the choice comes every time, nothing worse.
  }
}

/*
 * **One card plays at a time**, and the state lives here, not per card.
 * MusicKit has one queue; a second card taking it over must take the pause
 * button off the first. `useSyncExternalStore` in the card reads it.
 */
export interface MusicState {
  card: string | null
  playing: boolean
  signedIn: boolean
}

let state: MusicState = { card: null, playing: false, signedIn: false }
const listeners = new Set<() => void>()

function setState(next: MusicState): void {
  state = next
  for (const l of listeners) l()
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function snapshot(): MusicState {
  return state
}

/** Sign-in succeeded (or was found later): note it for the next visit. */
export function signedIn(): void {
  remember(true)
  setState({ ...state, signedIn: true })
}

/** Queue and play for one card. Throws; the caller writes which step failed. */
export async function playCard(mk: MusicKitInstance, card: string, queue: Record<string, string>): Promise<void> {
  try {
    await mk.setQueue(queue)
  } catch (error) {
    traceMusic('setQueue', error)
    throw error
  }
  try {
    await mk.play()
  } catch (error) {
    traceMusic('play', error)
    throw error
  }
  traceMusic('spielt')
  setState({ ...state, card, playing: true })
}

export async function pause(mk: MusicKitInstance): Promise<void> {
  await mk.pause()
  setState({ ...state, playing: false })
}

export async function resume(mk: MusicKitInstance): Promise<void> {
  try {
    await mk.play()
  } catch (error) {
    traceMusic('play', error)
    throw error
  }
  setState({ ...state, playing: true })
}

/** The way back: sign out, forget the note, stop playing. */
export async function signOut(mk: MusicKitInstance): Promise<void> {
  try {
    await mk.pause()
  } catch {
    // Nothing playing -- fine.
  }
  await mk.unauthorize()
  remember(false)
  traceMusic('abgemeldet')
  setState({ card: null, playing: false, signedIn: false })
}
